// Story 8.3 (ADO-2012), Task 11.5: a line a colleague removed while the Shopper was editing
// it, drawn where it was (card 03) as the design system's Removed Line inside the list's own
// row box, so it keeps the row's padding and divider (CartLineRow.module.css `.row`). The side
// panel clamps the name to two lines as its rows do; the review page wraps it in full.

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { RemovedLine } from '../design-system';
import type { ConflictViewAction } from './conflictCopy';
import { focusRowControl, type CartLineRowVariant, type RowControl } from './CartLineRow';
import { useBasketStore, type OrderSentNotice } from './useBasketStore';
import styles from './CartLineRow.module.css';

export interface RemovedCartLineProps {
  readonly skuCode: string;
  readonly name: string;
  readonly message: string;
  readonly action: ConflictViewAction;
  readonly variant: CartLineRowVariant;
  /** The review page's row (Story 8.11), for its padding. */
  readonly review?: boolean;
  /** Story 8.3 (ADO-2012), card 09: false for a refused send's mark, which its banner announces. */
  readonly announce?: boolean;
  readonly onAction: (skuCode: string, action: ConflictViewAction) => void;
}

export function RemovedCartLine({
  skuCode,
  name,
  message,
  action,
  variant,
  review = false,
  announce = true,
  onAction,
}: RemovedCartLineProps) {
  return (
    <li
      className={styles.row}
      data-variant={variant}
      data-sku={skuCode}
      data-review={review ? '' : undefined}
      data-removed=""
    >
      <RemovedLine
        productName={name}
        message={message}
        action={{ label: action.label, onClick: () => onAction(skuCode, action) }}
        clampName={variant === 'panel' || variant === 'panelMobile'}
        announce={announce}
      />
    </li>
  );
}

/**
 * Story 8.3 (ADO-2012): hand focus to a row's control once that row is on screen. "Add it
 * back" unmounts the focused button and the line returns a render later, so the request is
 * held and taken as soon as the control renders, as ProductCard's Add-to-stepper swap does.
 * Given the add (`settled`), a request whose add put no line in the cart is dropped, so the
 * line arriving later by another route (a colleague, a product card) does not take focus.
 * `untilLanded` (card 10's "Add to a new cart") also waits for the add to land: focus stays on
 * the action while the optimistic line is drawn, moves in the commit the line lands (the
 * action is still mounted then), and stays put when the add fails or is refused.
 */
export function usePendingRowFocus(
  scopeRef: React.RefObject<HTMLElement | null>,
): (
  skuCode: string,
  control: RowControl,
  settled?: Promise<unknown>,
  options?: { readonly untilLanded?: boolean },
) => void {
  const pending = useRef<{
    readonly skuCode: string;
    readonly control: RowControl;
    readonly untilLanded: boolean;
    /** The Order sent banner shown when the request was made (`untilLanded` only). */
    readonly notice: OrderSentNotice | null;
  } | null>(null);
  useLayoutEffect(() => {
    const request = pending.current;
    const scope = scopeRef.current;
    if (request === null || scope === null) return;
    const row = scope.querySelector(`li[data-sku="${CSS.escape(request.skuCode)}"]`);
    if (row === null || row.hasAttribute('data-removed')) return;
    if (request.untilLanded && !hasLanded(request.skuCode, request.notice)) return;
    focusRowControl(scope, request.skuCode, request.control);
    pending.current = null;
  });
  return (skuCode, control, settled, options) => {
    const untilLanded = options?.untilLanded === true;
    const request = {
      skuCode,
      control,
      untilLanded,
      notice: untilLanded ? useBasketStore.getState().orderSent : null,
    };
    pending.current = request;
    const settle = (landed: boolean): void => {
      if (pending.current !== request) return;
      if (!landed || !useBasketStore.getState().quantityBySku.has(skuCode)) pending.current = null;
    };
    // `untilLanded`: an add that did not land (it resolves anything but `true`) takes no focus.
    void settled?.then(
      (value) => settle(!untilLanded || value === true),
      () => settle(!untilLanded),
    );
  };
}

/** Story 8.3 (ADO-2012), card 10: the add of `skuCode` has landed: the write is no longer
 *  pending, and it was not refused or failed (no conflict and no error on the product, and the
 *  Order sent banner is still the one it was made from, or gone, rather than a new refusal's).
 *  An optimistic line drawn while the add is in flight has not landed, and neither has a row the
 *  cart already held that a failed add rolled back to. */
function hasLanded(skuCode: string, notice: OrderSentNotice | null): boolean {
  const { pending, lineConflicts, mutationErrors, orderSent } = useBasketStore.getState();
  return (
    !pending.has(skuCode) &&
    !lineConflicts.has(skuCode) &&
    !mutationErrors.has(skuCode) &&
    (orderSent === null || orderSent === notice)
  );
}

/**
 * Story 8.3 (ADO-2012), card 03: when the row the Shopper was using becomes a Removed Line, the
 * control they had focused goes with it and focus would drop to the page. It moves to the
 * Removed Line's "Add it back" instead. Only when focus was in that row and was lost: focus the
 * Shopper has put anywhere else stays where it is.
 */
function useFocusRemovedLineAction(scopeRef: React.RefObject<HTMLElement | null>): void {
  // The SKU of the row holding focus, kept as focus moves (the list can mount later). A Removed
  // Line counts too, so its action keeps focus if the review page draws it again.
  const focusedSku = useRef<string | null>(null);
  const moveFocusToRemovedLine = useCallback((): void => {
    const skuCode = focusedSku.current;
    const scope = scopeRef.current;
    if (skuCode === null || scope === null) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    const row = scope.querySelector(`li[data-sku="${CSS.escape(skuCode)}"][data-removed]`);
    row?.querySelector<HTMLElement>('button')?.focus();
  }, [scopeRef]);
  useEffect(() => {
    const onFocusIn = (event: FocusEvent): void => {
      const row = event.target instanceof Element ? event.target.closest('li[data-sku]') : null;
      const inScope = row !== null && scopeRef.current?.contains(row) === true;
      focusedSku.current = inScope ? row.getAttribute('data-sku') : null;
    };
    // Focus sent to the page (a click on blank space) was not lost by a row going away: forget
    // the row. Which it was is decided a frame later, after the render: Chrome sends focusout to
    // a removed control while it is still connected. A control still connected then, with focus
    // on the page, was left; one removed with its row was lost, and the layout effect below has
    // already moved focus to its Removed Line. The window losing focus keeps the control focused,
    // so the row is kept.
    let frame: number | null = null;
    const onFocusOut = (event: FocusEvent): void => {
      const target = event.target;
      if (event.relatedTarget !== null || !(target instanceof Node)) return;
      const skuCode = focusedSku.current;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        if (focusedSku.current !== skuCode) return;
        const active = document.activeElement;
        const onThePage = active === null || active === document.body;
        if (target.isConnected && onThePage) focusedSku.current = null;
      });
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [scopeRef]);
  useLayoutEffect(moveFocusToRemovedLine);
}

/**
 * Story 8.3 (ADO-2012): the Removed Line's action, "Add it back (M)": add the number the
 * Shopper picked (the designer's number rule) and hand focus to the line's quantity once it
 * is back. The add is a new change to the line, so it ends the message (the lifetime rule).
 * A row that becomes a Removed Line while the Shopper is using it hands focus to this action.
 */
export function useRemovedLineAction(
  scopeRef: React.RefObject<HTMLElement | null>,
): (skuCode: string, action: ConflictViewAction) => void {
  const add = useBasketStore((s) => s.add);
  const requestFocus = usePendingRowFocus(scopeRef);
  useFocusRemovedLineAction(scopeRef);
  return (skuCode, action) => {
    if (action.intent.type !== 'add') return;
    requestFocus(skuCode, 'quantity', add(skuCode, action.intent.quantity));
  };
}
